import {
  HarmObserved,
  OpeningHourSchedule,
  Player,
  POIClassification,
  SweepGeneratedStage,
  SweepSignature,
  SweepStatus,
  SweepType,
  SweepUpdateStage,
  User,
  UserType,
} from '@inplay/models';
import { TableHeader } from '../../component/table/table.type';
import moment from 'moment';
import { getSweepStatusDotColor } from '../../utils/helper';
import { Option } from '../../component/dropDownInput/dropDownInput.type';
import {
  SweepFilters,
  SweepKebabOption,
  SweepKey,
  SweepStage,
  SweepTableHeader,
  SweepTransition,
  UserRole,
} from './sweeps.type';
import ActionButton from '../../component/button/button';
import { Trans } from 'react-i18next';
import Close from '../../assets/close.png';
import Button from '../../component/button/button';
import { camelCase, isUndefined, startCase } from 'lodash';
import PlusIcon from '../../assets/plus.png';
import { SelectPlayerOption } from '../../component/addNewPlayerModal/addNewPlayerModal.type';
import { Sweep } from '../../utils/helper.type';
import { OptionWithLoading } from '../userManagement/userManagement.type';
import Loading from '../../assets/loading/loading';

export const getSweepTableHeader = (
  isTablet: boolean,
): TableHeader<Sweep>[] => {
  const headers: TableHeader<Sweep>[] = [
    {
      generateCellContent: (sweep: Sweep) => sweep?.sweepNumber ?? 0,
      header: isTablet ? SweepTableHeader.NO : SweepTableHeader.SWEEP_NO,
      sortable: true,
      getCellClassName: (sweep) => sweepTypeColorMap[sweep.type],
      key: SweepKey.SWEEP_NO,
    },
    {
      generateCellContent: (sweep: Sweep) => (
        <>
          <span
            className={`w-2 h-2 mr-2 rounded-full inline-block ${getSweepStatusDotColor(
              sweep.status,
            )}`}
          />
          {sweep.status.charAt(0).toUpperCase() +
            sweep.status.slice(1).toLowerCase()}
        </>
      ),
      header: SweepTableHeader.STATUS,
      getCellClassName: (sweep) => sweepTypeColorMap[sweep.type],
    },
    {
      generateCellContent: (sweep: Sweep) => sweep?.type,
      header: SweepTableHeader.SWEEP_TYPE,
      getCellClassName: (sweep) => sweepTypeColorMap[sweep.type],
    },
    {
      generateCellContent: (sweep: Sweep) =>
        sweep.createdOn ? moment(sweep.createdOn).format('HH:mm') : '-',
      header: SweepTableHeader.TIME,
      getCellClassName: (sweep) => sweepTypeColorMap[sweep.type],
    },
    {
      generateCellContent: (sweep: Sweep) => `Hour ${sweep.hour}`,
      header: SweepTableHeader.HOUR,
      getCellClassName: (sweep) => sweepTypeColorMap[sweep.type],
    },
    {
      generateCellContent: (sweep: Sweep) =>
        sweep.players.filter((player) => player.presentDuringSweep).length,
      header: SweepTableHeader.NO_OF_PLAYER,
      getCellClassName: (sweep) => sweepTypeColorMap[sweep.type],
    },
    {
      generateCellContent: (sweep: Sweep) => {
        const poiPlayers = sweep.players.filter(
          (player) => player.poiClassification === POIClassification.POI,
        );

        return poiPlayers.length > 0 ? `Yes (${poiPlayers.length})` : '-';
      },
      header: SweepTableHeader.HARM_OBSERVED,
      getCellClassName: (sweep) => sweepTypeColorMap[sweep.type],
    },
    {
      generateCellContent: (sweep: Sweep) =>
        sweep.completedBy?.firstName
          ? `${sweep.completedBy.firstName} ${sweep.completedBy.lastName}`
          : '-',
      header: SweepTableHeader.COMPLETED_BY,
      getCellClassName: (sweep) => sweepTypeColorMap[sweep.type],
    },
    {
      generateCellContent: (sweep: Sweep) =>
        sweep.reviewedBy?.firstName
          ? `${sweep.reviewedBy.firstName} ${sweep.reviewedBy.lastName}`
          : '-',
      header: SweepTableHeader.REVIEWED_BY,
      getCellClassName: (sweep) => sweepTypeColorMap[sweep.type],
    },
  ];

  return isTablet ? headers.slice(0, headers.length - 1) : headers;
};

export const sweepTypeColorMap: Record<SweepType, string> = {
  [SweepType.Obligatory]: 'text-[#030303]',
  [SweepType.Voluntary]: 'text-[#6A6A6A]',
};

/**
 * Generates a list of kebab menu options for a specific sweep row in a table,
 * based on the sweep's status and the current loading state.
 *
 * @template T - Generic type representing the user or sweep object.
 * @param user - The sweep object containing at least a `status` field.
 * @param loadingMenu - An optional object representing the current loading state of a menu item,
 *                      containing the row index and the action (option value) being loaded.
 * @param rowIndex - The index of the current row for which options are being generated.
 * @returns An array of kebab menu options with loading flags (`loading: true/false`)
 *          depending on whether a particular option is currently loading.
 *
 * Options are conditionally added based on the sweep's status:
 * - If `Completed`, includes: Edit, View/Edit History, Download.
 * - If `Missed`, includes: Download.
 */

export const getSweepKebabOptions = <T, Kebabaction extends string = string>(
  sweep: T,
  loadingMenu: { rowIndex: number; action: Kebabaction } | null,
  rowIndex: number,
): OptionWithLoading<SweepKebabOption>[] => {
  const { status } = sweep as { type: string; status: string };
  const options: OptionWithLoading<SweepKebabOption>[] = [];
  const addOption = (option: SweepKebabOption) => {
    options.push({
      label: option,
      value: camelCase(option),
      loading: false,
    });
  };

  if (status === SweepStatus.Completed) {
    addOption(SweepKebabOption.Edit);
    addOption(SweepKebabOption.ViewEditHistory);
    addOption(SweepKebabOption.Download);
  }

  if (status === SweepStatus.Missed) {
    addOption(SweepKebabOption.Edit);
    addOption(SweepKebabOption.ViewEditHistory);
    addOption(SweepKebabOption.Download);
  }

  return options.map((option: OptionWithLoading<SweepKebabOption>) => {
    return {
      ...option,
      loading:
        loadingMenu?.rowIndex === rowIndex &&
        loadingMenu?.action === option.value,
    };
  });
};

/**
 * Renders a group of action buttons (Download, Delete) along with a summary of selected items.
 *
 * @param selectedCount - Number of selected items to show in the summary.
 * @param onClose - Callback triggered when the close icon is clicked.
 * @param onDelete - Callback triggered when the "Delete" button is clicked.
 * @param onDownload - Callback triggered when the "Download" button is clicked.
 *
 * @returns JSX.Element containing the action buttons and selection summary.
 */
export const renderActionButtons = (
  selectedCount: number,
  reportLabel: string,
  onClose?: () => void,
  onDelete?: () => void,
  onDownload?: () => void,
  showDownloadLoading?: boolean,
) => {
  return (
    <div className="flex items-center flex-wrap gap-2">
      {
        <ActionButton
          className="px-8 py-2 ml-2 bg-white hover:bg-white hover:text-[#1A1A1A] text-[#1A1A1A] border border-black rounded-full text-sm"
          onClick={onDownload}
        >
          {showDownloadLoading ? (
            <Loading height={20} width={20} />
          ) : (
            <Trans i18nKey="sweeps.actionButton.download" />
          )}
        </ActionButton>
      }

      {
        <ActionButton
          className="px-8 py-2 ml-2 bg-white hover:bg-white hover:text-[#E50000] text-[#E50000] border border-black rounded-full text-sm"
          onClick={onDelete}
        >
          <Trans i18nKey="userManagement.actionButton.delete" />
        </ActionButton>
      }

      <span className="flex items-center text-[12px] text-[#092344] lowercase">
        <Trans
          i18nKey="sweeps.selectedSweepReport"
          values={{
            count: selectedCount,
            report: reportLabel,
          }}
        />{' '}
        <img
          onClick={onClose}
          src={Close}
          alt="Close"
          className="w-3 h-3 cursor-pointer ml-3"
        />
      </span>
    </div>
  );
};

export const getMidnightTimestampsForDate = (
  selectedDate: Date | undefined,
) => {
  const date: Date = new Date(selectedDate ?? '');
  const startDate: Date = new Date(date);
  startDate.setHours(0, 0, 0, 0);

  const endDate: Date = new Date(startDate);
  endDate.setSeconds(endDate.getSeconds() + 86400 - 1);

  return {
    startTime: Math.floor(startDate.getTime() / 1000),
    endTime: Math.floor(endDate.getTime() / 1000),
  };
};

export const generateOpeningHourOptions = (
  date: Date | undefined,
  hourData: OpeningHourSchedule | void | undefined | null,
): Option[] => {
  if (!hourData) return [];

  const targetDate = date ? moment(date) : moment();
  const weekday = targetDate
    .format('dddd')
    .toLowerCase() as keyof OpeningHourSchedule;

  const schedule = hourData[weekday];
  if (!schedule) return [];

  const { openingTime, closingTime } = schedule;

  const start = moment(openingTime, 'HH:mm');
  let end = moment(closingTime, 'HH:mm');

  // Handle overnight closing (e.g., closingTime "00:00" or earlier than openingTime)
  if (!end.isAfter(start)) {
    end.add(1, 'day');
  }

  const options: Option[] = [];
  let hourSlot = 1;
  let slotStart = start.clone();

  while (slotStart < end) {
    const slotEnd = slotStart.clone().add(1, 'hour');
    if (slotEnd > end) break;

    // Format hours as numbers (no leading zeros)
    const startHour = slotStart.hour();
    const endHour = slotEnd.hour();

    const label = `Hour ${hourSlot} (${startHour}-${endHour})`;

    options.push({
      label,
      value: hourSlot.toString(), // <-- Use hourSlot as value
    });

    slotStart = slotEnd;
    hourSlot++;
  }

  return options;
};

export const InitalFilters: SweepFilters = {
  dateRange: { from: new Date(), to: undefined },
  time: { from: undefined, until: undefined },
  hours: [],
  status: [],
  sweepType: [],
  harmObserved: undefined,
  completedBy: [],
};

export const handleSweepFilters = (
  sweepsSummary: Sweep[],
  sweepSearchQuery: string,
  filters: SweepFilters,
): Sweep[] => {
  const query: string = sweepSearchQuery.trim().toLowerCase();

  const matchesName = (person?: SweepSignature): boolean => {
    if (!person) return false;
    const firstName: string = person.firstName ?? '';
    const lastName: string = person.lastName ?? '';
    const fullName: string = `${firstName} ${lastName}`.toLowerCase();
    return (
      fullName.includes(query) ||
      firstName.toLowerCase().includes(query) ||
      lastName.toLowerCase().includes(query)
    );
  };

  const filtered: Sweep[] = sweepsSummary.filter((sweep: Sweep): boolean => {
    // --- search query match ---
    const typeMatch: boolean = sweep.type.toLowerCase().includes(query);
    const completedByQueryMatch: boolean = matchesName(sweep.completedBy);
    const reviewedByQueryMatch: boolean = matchesName(sweep.reviewedBy);
    const matchesSearchQuery: boolean =
      query.length === 0 ||
      typeMatch ||
      completedByQueryMatch ||
      reviewedByQueryMatch;

    // --- filters ---
    const matchesStatus: boolean =
      filters.status.length === 0 ||
      filters.status.includes(
        sweep.status && (sweep.status.toLowerCase() as SweepStatus),
      );

    const matchesType: boolean =
      filters.sweepType.length === 0 || filters.sweepType.includes(sweep.type);

    const matchesHour: boolean =
      filters.hours.length === 0 || filters.hours.includes(String(sweep.hour));

    const matchesTimeRange: boolean = (() => {
      if (!filters.time.from && !filters.time.until) return true;

      const scheduledTime: string = moment(
        sweep?.scheduledTime ?? sweep.createdOn,
      ).format('HH:mm');
      const from: string = filters.time.from ?? '00:00';
      const until: string = filters.time.until ?? '23:59';

      return scheduledTime >= from && scheduledTime <= until;
    })();

    const matchesCompletedBy: boolean =
      filters.completedBy.length === 0 ||
      filters.completedBy.some((name: string) => {
        const fullName: string = `${sweep.completedBy?.firstName ?? ''} ${
          sweep.completedBy?.lastName ?? ''
        }`.toLowerCase();
        return fullName.includes(name.toLowerCase());
      });

    const matchesHarmObserved: boolean = (() => {
      if (!filters.harmObserved) return true;

      const hasPOI: boolean = sweep.players.some(
        (player) => player.poiClassification === POIClassification.POI,
      );

      switch (filters.harmObserved) {
        case HarmObserved.YES:
          return hasPOI;
        case HarmObserved.NO:
          return !hasPOI;
        default:
          return true;
      }
    })();

    return (
      matchesSearchQuery &&
      matchesStatus &&
      matchesType &&
      matchesHour &&
      matchesTimeRange &&
      matchesCompletedBy &&
      matchesHarmObserved
    );
  });

  return filtered;
};

export const sweepHeader = (selectedSweep: Sweep | null) => {
  const isMissed: boolean = selectedSweep?.status === SweepStatus.Missed;

  const poiCount: number =
    selectedSweep?.players?.filter(
      (player: Player) => player.poiClassification === POIClassification.POI,
    ).length ?? 0;

  return (
    <div className={`ml-10 mr-7 ${isMissed ? 'bg-black py-2' : 'py-3'}`}>
      <div
        className={`grid text-base font-medium mx-6 items-center text-SWEEP_TEXT_COLOR ${
          isMissed ? 'text-white grid-cols-6' : 'grid-cols-5'
        }`}
      >
        <div className="text-left">
          <span className="font-semibold">
            <Trans i18nKey={'sweeps.filters.time'} />
          </span>{' '}
          <span className="font-normal ml-2">
            {selectedSweep?.createdOn
              ? moment(selectedSweep.createdOn).format('HH:mm')
              : '-'}
          </span>
        </div>

        <div className="text-center">
          <span className="font-semibold">
            {' '}
            <Trans i18nKey={'sweeps.report.hour'} />
          </span>{' '}
          <span className="font-normal ml-2">{selectedSweep?.hour}</span>
        </div>

        <div className="text-center">
          <span className="font-semibold">
            <Trans i18nKey={'sweeps.report.noOfPlayer'} />
          </span>{' '}
          <span className="font-normal ml-2">
            {
              selectedSweep?.players.filter(
                (player: Player) => player.presentDuringSweep,
              ).length
            }
          </span>
        </div>

        <div className="text-center">
          <span className="font-semibold">
            <Trans i18nKey={'sweeps.filters.harmObserved'} />
          </span>{' '}
          <span className="font-normal ml-2">
            {poiCount > 0 ? `Yes (${poiCount})` : 'N/A'}
          </span>
        </div>

        <div className="text-right">
          <span className="font-semibold">
            {' '}
            <Trans i18nKey={'sweeps.report.tag'} />
          </span>{' '}
          <span className="font-normal ml-2">
            {selectedSweep?.tag || 'N/A'}
          </span>
        </div>

        <div className="text-right">
          {isMissed && (
            <div className="bg-white text-black font-bold w-36 py-2 rounded-[10px] text-center ml-auto">
              <Trans i18nKey={'sweeps.report.missedSweep'} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Renders the Manager/Admin Signature Section in the Sweep Report.
 *
 * This section allows managers (non-venue staff users) to:
 * - View or add notes related to the sweep.
 * - View, add, or update their signature.
 *
 * Edit permissions are allowed only when:
 * - The user is **not** a venue staff (`VENUE_STAFF` or `VENUE_BRANCH_LOGIN`)
 * - The logged-in user is either the reviewer or the sweep has not yet been reviewed
 *
 * If a signature is already available, the reviewer’s name and signature date/time are displayed.
 *
 * @param {string} userType - Role of the currently logged-in user.
 * @param {Sweep | null} selectedSweep - The sweep data currently being viewed.
 * @param {string} managerNote - The note written by the manager/reviewer.
 * @param {(value: string) => void} setManagerNote - Setter function to update the manager note.
 * @param {string} loggedUserId - ID of the currently logged-in user.
 * @param {() => void} handleAddManagerSignature - Function triggered to add or update manager signature.
 *
 * @returns {JSX.Element} The rendered Manager Signature Section.
 */
export const renderManagerSignatureSection = (
  userType: string,
  selectedSweep: Sweep | undefined,
  managerNote: string,
  setManagerNote: (value: string) => void,
  loggedUserId: string,
  handleAddSignature: (userRole: UserRole) => void,
  handleDeleteSignature: (userRole: UserRole) => void,
  handleEditSignature: (userRole: UserRole) => void,
) => {
  const isVenueStaff: boolean =
    userType === UserType.VENUE_STAFF ||
    userType === UserType.VENUE_BRANCH_LOGIN;

  const reviewedBy: SweepSignature | undefined = selectedSweep?.reviewedBy;

  const canEdit: boolean =
    isUndefined(reviewedBy) || reviewedBy._id === loggedUserId;
  const isEditable: boolean = !isVenueStaff && canEdit;

  return (
    <div className={`${isVenueStaff ? 'w-[40%]' : 'w-[60%]'} bg-[#F5F5F5] p-4`}>
      <div className="flex justify-between items-start gap-6 text-SWEEP_TEXT_COLOR">
        <div className="flex-1">
          <p className="font-semibold text-base mb-4">
            <Trans i18nKey="sweeps.managerAdminUse" />
          </p>
          <hr />
          <p className="font-medium text-base mt-3">
            <Trans i18nKey="sweeps.notes" />
          </p>

          {renderSignatureNoteView({
            isEditable: !isVenueStaff && isEditable,
            signatureNote: managerNote,
            setSignatureNote: setManagerNote,
            showNote: reviewedBy?.notes || '',
          })}
        </div>

        <div className="flex flex-col items-start">
          <p className="font-semibold text-base mb-4 whitespace-nowrap">
            <Trans i18nKey="sweeps.managerAdminSignature" />
          </p>

          {reviewedBy?.signature ? (
            <>
              {renderSignatureView(reviewedBy)}

              {!isVenueStaff &&
                isEditable &&
                renderEditDeleteButtons(
                  () => handleEditSignature(UserRole.MANAGER),
                  () => handleDeleteSignature(UserRole.MANAGER),
                )}
            </>
          ) : (
            <>
              {isVenueStaff && (
                <p className="text-[#AAAAAA] text-base font-normal">
                  <Trans i18nKey="onboarding.noSignature" />
                </p>
              )}
              {!isVenueStaff &&
                !reviewedBy &&
                renderAddSignatureView(() =>
                  handleAddSignature(UserRole.MANAGER),
                )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Renders a fallback UI for when a user signature is missing.
 *
 * This section includes a text message indicating that no signature is available,
 * and a button to allow the user to add their signature.
 *
 * @param onClick - A callback function to handle the button click (e.g., open signature modal).
 * @returns JSX.Element - The rendered fallback signature section.
 */
export const renderAddSignatureView = (
  handleAddSignature: () => void,
): JSX.Element => (
  <>
    <div className="text-[#AAAAAA] text-base font-normal">
      <Trans i18nKey="onboarding.noSignature" />
    </div>
    <Button
      onClick={handleAddSignature}
      className="px-16 py-2 text-sm bg-THEME_COLOR text-white rounded-full mt-3"
    >
      <Trans i18nKey="universalStaffLoginModal.add" />
    </Button>
  </>
);

/**
 * Renders a vertical stack of "Edit" and "Delete" buttons.
 *
 * - The "Edit" button is styled with a white background and triggers the `onEdit` callback when clicked.
 * - The "Delete" button is styled with a red text color and border, and triggers the `onDelete` callback when clicked.
 *
 * This is typically used in signature sections or user management panels for modifying or removing entries.
 *
 * @param onEdit - Callback function to invoke when the "Edit" button is clicked.
 * @param onDelete - Callback function to invoke when the "Delete" button is clicked.
 * @returns JSX.Element containing two styled buttons with appropriate actions.
 *
 * @example
 * ```tsx
 * {renderEditDeleteButtons(
 *   () => handleEditSignature(UserRole.STAFF),
 *   () => handleDeleteSignature(UserRole.STAFF)
 * )}
 * ```
 */
export const renderEditDeleteButtons = (
  onEdit: () => void,
  onDelete: () => void,
): JSX.Element => (
  <div className="flex flex-col gap-2 mt-5">
    <Button
      onClick={onEdit}
      className="px-16 py-2 text-sm bg-white text-black rounded-full"
    >
      <Trans i18nKey="universalStaffLoginModal.edit" />
    </Button>
    <Button
      onClick={onDelete}
      className="px-16 py-2 text-sm bg-transparent text-[#D90000] border-2 border-[#C1C1C1] rounded-full"
    >
      <Trans i18nKey="userManagement.actionButton.delete" />
    </Button>
  </div>
);

/**
 * Renders the signature view section including the signature image,
 * signer’s full name, and the signed timestamp.
 *
 * This function is used to conditionally render a user's signature section
 * (e.g., reviewedBy or completedBy) only if a signature is present.
 *
 * @param selectedSweepSignature - An object containing signature data such as
 * signature image, signed date, first name, and last name.
 *
 * @returns A JSX element displaying the signature section, or null if no signature exists.
 */
export const renderSignatureView = (
  selectedSweepSignature: SweepSignature | undefined,
): JSX.Element | null => {
  if (!selectedSweepSignature?.signature) return null;

  return (
    <div>
      <img
        src={selectedSweepSignature?.signature}
        alt="Signature"
        className="h-10 mb-2 object-contain"
      />
      {selectedSweepSignature.signedDate && (
        <>
          <p className="text-[16px] font-semibold text-[#151740] mb-1">
            {selectedSweepSignature.firstName} {selectedSweepSignature.lastName}
          </p>
          <div className="text-[#383838] text-sm">
            {moment(selectedSweepSignature.signedDate).format(
              'hh:mm A, DD/MM/YYYY',
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Renders the signature note section for the sweep review panel.
 *
 * If `isEditable` is `true`, a textarea is displayed allowing the user to input or edit
 * the manager/admin note. Otherwise, the saved note is displayed in a styled paragraph.
 * If no note exists, a placeholder `--` is shown.
 *
 * @param props - Object containing:
 * @param props.isEditable - Determines whether the note field is editable.
 * @param props.managerNote - The current note value when in editable mode.
 * @param props.setManagerNote - Function to update the `managerNote` state.
 * @param props.selectedSweep - The selected sweep object containing review details.
 *
 * @returns A JSX element that conditionally renders a textarea or paragraph based on edit mode.
 */
export const renderSignatureNoteView = (props: {
  isEditable: boolean;
  signatureNote: string;
  setSignatureNote: (value: string) => void;
  showNote: string;
}) => {
  return props.isEditable ? (
    <textarea
      className="bg-white h-32 w-full focus:outline-none mt-3 rounded-lg p-2 text-sm text-[#151740]"
      value={props.signatureNote}
      onChange={(e) => props.setSignatureNote(e.target.value)}
    />
  ) : (
    <p className="text-sm text-[#151740] mt-3 h-32 whitespace-pre-wrap">
      {props.showNote?.trim() || <span className="text-[#151740]">--</span>}
    </p>
  );
};

/**
 * Renders the Staff Signature Section of the Sweep Report.
 *
 * This section is shown to venue staff and allows them to:
 * - View or add notes related to the sweep.
 * - View or sign their signature if they are allowed.
 *
 * The UI is conditionally editable based on:
 * - If the user is a venue staff (`VENUE_STAFF` or `VENUE_BRANCH_LOGIN`)
 * - If the sweep is not marked as "Missed"
 * - If the logged-in user is the one who completed the sweep or no one has completed it yet
 *
 * If the sweep is of type "Missed", the section becomes disabled (visually grayed out and unclickable).
 *
 * @param {string} userType - Type of the current user (e.g., VENUE_STAFF).
 * @param {Sweep | null} selectedSweep - The currently selected sweep data.
 * @param {string} staffNote - The staff note entered or fetched from completed sweep.
 * @param {(value: string) => void} setStaffNote - Function to update the staff note.
 * @param {string} loggedUserId - ID of the currently logged-in user.
 * @param {() => void} handleStaffSignature - Callback to open the signature modal or capture signature.
 *
 * @returns {JSX.Element} The rendered Staff Signature Section.
 */
export const renderStaffSignatureSection = (
  userType: string,
  selectedSweep: Sweep | undefined,
  staffNote: string,
  setStaffNote: (value: string) => void,
  loggedUserId: string,
  handleAddSignature: (userRole: UserRole) => void,
  handleDeleteSignature: (userRole: UserRole) => void,
  handleEditSignature: (userRole: UserRole) => void,
) => {
  const isVenueStaff: boolean =
    userType === UserType.VENUE_STAFF ||
    userType === UserType.VENUE_BRANCH_LOGIN;

  const isMissed: boolean = selectedSweep?.status === SweepStatus.Missed;
  const hasCompletedBy = !!selectedSweep?.completedBy;

  const canEdit: boolean =
    !hasCompletedBy || selectedSweep?.completedBy?._id === loggedUserId;

  const isEditable: boolean = canEdit && !isMissed;

  return (
    <div
      className={`${isVenueStaff ? 'w-[60%]' : 'w-[40%]'} p-5 ${
        isMissed
          ? 'bg-[#EDEDED] opacity-60 pointer-events-none'
          : 'bg-[#FAFAFA]'
      }`}
    >
      <div className="flex justify-between items-start gap-6 text-SWEEP_TEXT_COLOR">
        <div className="flex-1">
          <p className="font-semibold text-base mb-4">
            <Trans i18nKey="sweeps.staffUse" />
          </p>
          <hr />
          <p className="font-medium text-base mt-3">
            <Trans i18nKey="sweeps.notes" />
          </p>
          {renderSignatureNoteView({
            isEditable,
            signatureNote: staffNote,
            setSignatureNote: setStaffNote,
            showNote: selectedSweep?.completedBy?.notes || '',
          })}
        </div>

        <div className="flex flex-col items-start">
          <p className="font-semibold text-base mb-4 whitespace-nowrap">
            <Trans i18nKey="sweeps.staffSignature" />
          </p>

          {/* ONLY check if completedBy is missing */}
          {!selectedSweep?.completedBy ? (
            renderAddSignatureView(() => handleAddSignature(UserRole.STAFF))
          ) : (
            <>
              {selectedSweep?.completedBy?.signature ? (
                <>
                  {renderSignatureView(selectedSweep?.completedBy)}
                  {isEditable && (
                    <>
                      {renderEditDeleteButtons(
                        () => handleEditSignature(UserRole.STAFF),
                        () => handleDeleteSignature(UserRole.STAFF),
                      )}
                    </>
                  )}
                </>
              ) : selectedSweep?.completedBy &&
                isDeleteSignature(selectedSweep?.completedBy) ? (
                renderAddSignatureView(() => handleAddSignature(UserRole.STAFF))
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Renders the combined Signature Section containing both Staff and Manager/Admin sections.
 *
 * This section is split into:
 * - **Staff Signature Section**: Displayed for venue staff (if applicable), allowing them to add or view sweep notes and signatures.
 * - **Manager Signature Section**: Displayed for admin/manager users, allowing them to review the sweep, add notes, and sign off.
 *
 * Each section handles its own editability logic internally based on:
 * - User type
 * - Whether the sweep is missed
 * - Whether the current user is authorized to edit the section
 *
 * @param {string} userType - The role of the currently logged-in user (e.g., VENUE_STAFF, ADMIN).
 * @param {Sweep | null} selectedSweep - The selected sweep containing all sweep-related data.
 * @param {string} staffNote - The note added by the staff (if any).
 * @param {(value: string) => void} setStaffNote - Function to update the staff note.
 * @param {string} managerNote - The note added by the manager/admin (if any).
 * @param {(value: string) => void} setManagerNote - Function to update the manager note.
 * @param {string} loggedUserId - ID of the currently logged-in user to validate permissions.
 * @param {() => void} handleAddManagerSignature - Callback to trigger the add/edit manager signature flow.
 * @param {() => void} handleStaffSignature - Callback to trigger the add/edit staff signature flow.
 *
 * @returns {JSX.Element} A horizontally laid-out signature section displaying both staff and manager components.
 */

export const renderSignatureSection = (
  userType: string,
  selectedSweep: Sweep | undefined,
  staffNote: string,
  setStaffNote: (value: string) => void,
  managerNote: string,
  setManagerNote: (value: string) => void,
  loggedUserId: string,
  handleAddSignature: (userRole: UserRole) => void,
  handleDeleteSignature: (userRole: UserRole) => void,
  handleEditSignature: (userRole: UserRole) => void,
  isTablet: boolean,
) => {
  return (
    <div
      className={` ml-10 border-t-2 rounded-md ${
        isTablet ? 'w-[95%] overflow-auto' : 'w-[96%] overflow-hidden'
      }`}
    >
      <div className="flex md:min-w-[120%] lg:min-w-0">
        {renderStaffSignatureSection(
          userType,
          selectedSweep,
          staffNote,
          setStaffNote,
          loggedUserId,
          handleAddSignature,
          handleDeleteSignature,
          handleEditSignature,
        )}

        {renderManagerSignatureSection(
          userType,
          selectedSweep,
          managerNote,
          setManagerNote,
          loggedUserId,
          handleAddSignature,
          handleDeleteSignature,
          handleEditSignature,
        )}
      </div>
    </div>
  );
};

/**
 * Returns the appropriate set of Tailwind CSS classes based on the active state.
 *
 * @param isActive - A boolean indicating whether the button is in an active state.
 * @param activeClasses - The CSS class string to apply when `isActive` is true.
 * @param inactiveClasses - The CSS class string to apply when `isActive` is false.
 * @returns A string of CSS classes corresponding to the active or inactive state.
 *
 * @example
 * getToggleButtonClass(true, 'bg-red-600 text-white', 'bg-white text-black');
 * // returns 'bg-red-600 text-white'
 */
export const getToggleButtonClass = (
  isActive: boolean,
  activeToggleButtonCssClass: string,
  inactiveToggleButtonCssClass: string,
) => {
  return isActive ? activeToggleButtonCssClass : inactiveToggleButtonCssClass;
};

/**
 * Determines whether a given signature should be considered as "deleted" or empty.
 *
 * A signature is considered deleted if:
 * - The `signature` object is `undefined` or `null`, or
 * - All of its key properties (`firstName`, `lastName`, and `signature`) are missing or falsy.
 *
 * @param signature - The signature object to check.
 * @returns `true` if the signature is considered deleted or empty, otherwise `false`.
 */
export const isDeleteSignature = (signature?: SweepSignature) => {
  if (!signature) return true;

  return !signature.firstName && !signature.lastName && !signature.signature;
};

/**
 * Renders a clickable "Add New Player" button with a plus icon.
 *
 * @param handleAddNewPlayer - Callback function triggered when the button is clicked.
 * @returns JSX.Element - A styled div acting as a button.
 */
export const renderAddNewPlayerButton = (handleAddNewPlayer: () => void) => {
  return (
    <div
      onClick={handleAddNewPlayer}
      className="flex items-center gap-2 text-base text-SWEEP_TEXT_COLOR font-medium px-10 py-6 cursor-pointer"
    >
      <div className="w-8 h-8 rounded-full bg-black text-white flex items-center justify-center">
        <img src={PlusIcon} alt="Plus Icon" className="w-4 h-4" />
      </div>
      <Trans i18nKey={'sweeps.addNewPlayer'} />
    </div>
  );
};

/**
 * Renders a radio button group to allow the user to choose between a randomly generated player
 * and an existing player profile. Displays the random ID and a label for the existing option.
 *
 * @param selectedOption - The currently selected player option ('random' or 'existing').
 * @param onChange - Callback function to update the selected option when changed.
 * @param randomID - The ID to display when the "Random" option is shown.
 * @returns A JSX element containing the radio options.
 */
/**
 * Renders a player selection radio group with two options:
 * 1. A randomly generated ID
 * 2. An existing player profile
 *
 * @param selectedOption - The currently selected option value ("random" or "existing")
 * @param onChange - Callback when the radio selection changes
 * @param randomID - The label used for the "random" option (usually a generated ID)
 */
export const renderPlayerSelection = (
  selectedOption: SelectPlayerOption,
  onChange: (value: SelectPlayerOption) => void,
  randomID?: string,
) => {
  const playerSelectionOptions = [
    { label: randomID, value: SelectPlayerOption.Random },
    { label: 'Existing player profile', value: SelectPlayerOption.Existing },
  ];

  return (
    <div className="space-y-3">
      {playerSelectionOptions.map(({ label, value }) => (
        <label
          key={value}
          className="flex items-center gap-2 cursor-pointer text-[#1A1A1A] text-base"
        >
          <input
            type="radio"
            name="playerOption"
            value={value}
            checked={selectedOption === value}
            onChange={() => {
              if (selectedOption !== value) {
                onChange(value);
              }
            }}
            className="form-radio accent-blue-600"
          />
          <span>{label}</span>
        </label>
      ))}
    </div>
  );
};

// Convert ISO string to minutes (from midnight)
export const getMinutesFromTimeString = (isoTime: string): number => {
  const date = new Date(isoTime);
  return date.getHours() * 60 + date.getMinutes();
};

// Convert minutes to time string like "9:00 AM"
export const formatTime = (totalMinutes: number): string => {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Generate time slots
export const generateTimeOptions = (
  start: number,
  end: number,
  interval = 30,
): string[] => {
  const options: string[] = [];
  for (let mins = start; mins <= end; mins += interval) {
    options.push(formatTime(mins));
  }
  return options;
};

/**
 * Renders a single "Edit History" stage, which shows:
 * - The date and time when the update happened
 * - The action that was taken (e.g. "Edited", "Approved")
 * - Who performed the update (user or signature)
 * - Any transitions (state changes) related to the update
 *
 * @param updatedAt - The timestamp when the change occurred
 * @param action - The action taken (e.g. "edit", "approve")
 * @param transitions - Optional list of state transitions for this stage
 * @param updatedBy - The user or signature who made the change
 *
 * @returns JSX element showing the edit history stage
 */
export const renderEditHistoryStage = (
  updatedAt: Date,
  action: string,
  transitions?: SweepTransition[],
  updatedBy?: User | SweepSignature,
) => {
  return (
    <div className="flex flex-col pt-5 pb-5">
      <div className="text-sm text-[#767676] font-semibold">
        {moment(updatedAt).format('DD/MM/YYYY, hh:mm A')}
        {action !== SweepUpdateStage.GENERATED && (
          <>
            {' — '}
            {startCase(action)} <Trans i18nKey="common.by" />{' '}
            {!isUndefined(updatedBy) && renderUpdatedByName(updatedBy)}
          </>
        )}
      </div>

      {transitions && transitions.length > 0 && (
        <div className="mt-3 space-y-2">
          {transitions.map((transition: SweepTransition, index: number) => (
            <div key={index} className="flex  text-sm">
              <div className="font-semibold text-[#1A1A1A]">
                {startCase(transition.action)}
              </div>

              <p className="text-[#1A1A1A] ml-3">
                {formatTransition(transition.transition) || ''}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Renders the full name of the user or sweep signature who last updated something.
 *
 * - Accepts an object of type `User` or `SweepSignature`.
 * - Safely handles cases where `firstName` or `lastName` may be missing.
 * - Returns a trimmed full name string (e.g., `"John Doe"`).
 * - Returns an empty string if `updatedBy` is `null` or `undefined`.
 *
 * @param updatedBy - The user or sweep signature object containing `firstName` and `lastName`.
 * @returns The concatenated and trimmed full name, or an empty string if not available.
 */
export const renderUpdatedByName = (
  updatedBy: User | SweepSignature,
): string => {
  const name: string = `${updatedBy.firstName} ${updatedBy.lastName}`;
  return name.trim();
};

/**
 * Renders a list of sweep update stages in the UI.
 *
 * - Accepts an array of `SweepStage` objects, each representing a stage in the sweep update history.
 * - For each stage, it calls `renderEditHistoryStage` to display detailed information including:
 *   - The update timestamp (`updatedAt`)
 *   - The stage name or action (`stage`)
 *   - Optional transitions (`transitions`)
 *   - The user or signature who performed the update (`updatedBy`)
 * - Wraps each rendered stage in a `<div>` with a unique `key`.
 * - Adds a horizontal line `<hr />` after all stages for visual separation.
 * - Returns `null` if the input array is `null` or `undefined`.
 *
 * @param sweepUpdatedStages - Array of `SweepStage` objects to render.
 * @returns JSX element displaying the sweep update stages, or `null` if no stages are provided.
 */
export const renderSweepUpdatedStage = (sweepUpdatedStages: SweepStage[]) => {
  if (sweepUpdatedStages.length === 0) return null;
  return (
    <>
      {sweepUpdatedStages.map(
        (sweepUpdatedStage: SweepStage, index: number) => (
          <div key={index}>
            {renderEditHistoryStage(
              sweepUpdatedStage?.updatedAt,
              sweepUpdatedStage?.stage,
              sweepUpdatedStage?.transitions,
              sweepUpdatedStage?.updatedBy,
            )}
          </div>
        ),
      )}
      <hr />
    </>
  );
};

/**
 * Renders a single completed sweep stage in the UI.
 *
 * - Accepts a `SweepGeneratedStage` object representing a completed stage in the sweep history.
 * - Calls `renderEditHistoryStage` to display detailed information including:
 *   - The update timestamp (`updatedAt`)
 *   - The stage name or action (`stage`)
 *   - Optional transitions (`transitions`)
 * - Wraps the rendered stage in a `<div>` and adds a horizontal line `<hr />` below it for visual separation.
 *
 * @param completedStage - The `SweepGeneratedStage` object to render.
 * @returns JSX element displaying the completed sweep stage.
 */
export const renderSweepCompletedStage = (
  completedStage: SweepGeneratedStage,
) => {
  return (
    <div>
      {renderEditHistoryStage(
        completedStage?.updatedAt,
        completedStage?.stage,
        completedStage?.transitions,
      )}
      <hr />
    </div>
  );
};

/**
 * Formats a transition string by replacing Unicode arrow characters with a visible arrow.
 *
 * - Replaces all occurrences of the Unicode right arrow (`\u2192`) in the input string with the literal arrow character (`→`).
 * - Useful for displaying transitions in a more readable format in the UI.
 *
 * @param transition - The string containing transition information, potentially with Unicode arrows.
 * @returns The formatted string with Unicode arrows replaced by visible arrow characters.
 */
export const formatTransition = (transition: string): string => {
  const updatedArrowString = transition.replace(/\\u2192/g, '→');
  const shortenedPlayerUUIDsString = updatedArrowString.replace(
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
    (uuid) => uuid.slice(0, 5),
  );

  return shortenedPlayerUUIDsString;
};

/**
 * Convert base64 string to PDF and trigger download
 * @param base64String base64 encoded PDF string
 * @param fileName name of the file to download
 */
